import { StyleSheet, View, Modal, TouchableOpacity, ActivityIndicator, ScrollView, Linking, Image, Platform } from "react-native";
import { UploadMedia } from "@/components/media/UploadMedia";
import { CapturePhoto } from "@/components/media/CapturePhoto";
import { MediaSelector } from "@/components/media/MediaSelector";
import { MediaModel } from "common247";
import { PlatformDetails } from "@/components/profile/PlatformDetails";
import { Ionicons } from "@expo/vector-icons";
import { AppVersion, ProductVersion, ApiVersion } from "@/versions";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { useAuth } from "@components/auth/useAuth";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedInput } from "@/components/ThemedInput";
import { CustomAlert } from "@/components/ThemedAlert";
import { useFirebase } from "@/components/firebase/useFirebase";
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, linkWithCredential, User, updateProfile, updateEmail } from "firebase/auth";
import { updateUserFunc, updateProfilePicture } from "@/services/firebase/user-functions";
import { connectGoogleDriveStorage, disconnectGoogleDriveStorage } from "@/services/firebase/media-functions";
import { createProfileImageVariants } from "@/services/image-resizer";
import { InappropriateContentModal } from "@/components/profile/InappropriateContentModal";
import { router, useLocalSearchParams } from "expo-router";
import { useState, useEffect } from "react";
import { validatePasswordPolicy } from "@/services/validation";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ThemedToggle } from "@/components/ThemedToggle";
import Constants from "expo-constants";
import { getAppConfig } from "@/services/globals";
import { getPrivacyPolicyUrl, getAboutUrl } from "@/services/environment-urls";
import { getServerStatus } from "@/services/firebase/status-functions";
import {
  isHintsGloballyEnabled,
  setHintsGloballyEnabled,
  resetHintsDismissal,
} from "@/services/hints-service";
import {
  checkLocationPermissions,
  requestLocationPermissions,
  ILocationPermissionStatus,
  setStartupGpsPromptDeclined,
} from "@/services/location-permissions";
import {
  hasUserConsentedToAnalytics,
  setAnalyticsConsent,
} from "@/services/analytics";

export default function ProfileScreen() {
  const { user, signOut, emailVerification, refreshUser } = useAuth();
  const services = useFirebase();

  const [verified, setVerified] = useState(false);

  const { resetPassword } = useLocalSearchParams<{ resetPassword?: string }>();

  // Password change / link state hooks
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Profile edit states
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [email, setEmail] = useState(user?.email || "");
  const [reauthPassword, setReauthPassword] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // Inline validation error states
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [reauthPasswordError, setReauthPasswordError] = useState("");
  const [oldPasswordError, setOldPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  // Report Inappropriate Content state hooks
  const [showReportModal, setShowReportModal] = useState(false);

  // Profile photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(
    user?.photoUrl || (user as any)?.photoURL || null
  );

  useEffect(() => {
    const current = user?.photoUrl || (user as any)?.photoURL;
    if (current) {
      setPhotoUrl(current);
    }
  }, [user?.photoUrl, (user as any)?.photoURL]);

  // Product hints state & handlers
  const [hintsEnabled, setHintsEnabled] = useState(true);

  useEffect(() => {
    isHintsGloballyEnabled().then(setHintsEnabled).catch(() => {});
  }, []);

  // GPS / Location permission state
  const [gpsStatus, setGpsStatus] = useState<ILocationPermissionStatus | null>(null);
  const [requestingGps, setRequestingGps] = useState(false);

  const loadGpsStatus = async () => {
    try {
      const status = await checkLocationPermissions();
      setGpsStatus(status);
    } catch (err) {
      console.warn("[Profile] Failed to load GPS status:", err);
    }
  };

  useEffect(() => {
    loadGpsStatus();
  }, []);

  // Analytics & Telemetry Privacy Consent state
  const [analyticsConsent, setAnalyticsConsentState] = useState(false);
  const [savingConsent, setSavingConsent] = useState(false);

  useEffect(() => {
    hasUserConsentedToAnalytics()
      .then(setAnalyticsConsentState)
      .catch((err) => console.warn("[Profile] Failed to load analytics consent:", err));
  }, []);

  const handleToggleAnalyticsConsent = async (val: boolean) => {
    setSavingConsent(true);
    try {
      await setAnalyticsConsent(val);
      setAnalyticsConsentState(val);
      CustomAlert.alert(
        val ? "Analytics Consent Granted" : "Analytics Consent Revoked",
        val
          ? "Thank you! Analytical telemetry is now active to help improve app reliability and user experience."
          : "Analytical telemetry is now deactivated. No analytical metrics or performance telemetry will be collected."
      );
    } catch (err: any) {
      console.error("[Profile] Failed to update analytics consent:", err);
      CustomAlert.alert("Error", "Failed to update analytics consent preferences.");
    } finally {
      setSavingConsent(false);
    }
  };

  const handleForceRequestGps = async () => {
    setRequestingGps(true);
    try {
      // Clear declined state so future flows remain aware of user's active interest
      await setStartupGpsPromptDeclined(false);
      const res = await requestLocationPermissions(true);
      setGpsStatus(res);
      if (res.granted) {
        CustomAlert.alert(
          "GPS Permissions Granted",
          "Location access is active. High-accuracy GPS coordinates will now tag your memories and media."
        );
      } else {
        CustomAlert.alert(
          "Permission Not Granted",
          res.canAskAgain
            ? "Location permission was not granted. You can tap the button to try again."
            : "Location permission is disabled in system settings. Please open device settings to enable GPS.",
          Platform.OS !== "web" && !res.canAskAgain
            ? [
                { text: "Cancel", style: "cancel" },
                { text: "Open Settings", style: "default", onPress: () => Linking.openSettings() },
              ]
            : [{ text: "OK", style: "default" }]
        );
      }
    } catch (err: any) {
      CustomAlert.alert("Error", err?.message || "Failed to request GPS permissions.");
    } finally {
      setRequestingGps(false);
    }
  };

  // Server Environment state
  const [serverEnv, setServerEnv] = useState<string>("CONNECTING...");

  useEffect(() => {
    if (!services) return;
    getServerStatus(services)
      .then((status) => {
        setServerEnv(status.environment ? status.environment.toUpperCase() : "UNKNOWN");
      })
      .catch((err) => {
        console.error("Failed to fetch server status on profile:", err);
        setServerEnv("UNAVAILABLE");
      });
  }, [services]);

  // Client Environment resolver
  const clientEnv = (() => {
    try {
      return getAppConfig().environ.getName().toUpperCase();
    } catch {
      const extraEnv = Constants.expoConfig?.extra?.env;
      return extraEnv ? String(extraEnv).toUpperCase() : "LOCAL";
    }
  })();

  const privacyPolicyUrl = getPrivacyPolicyUrl(clientEnv);
  const aboutUrl = getAboutUrl(clientEnv);

  const handleToggleHints = async (val: boolean) => {
    setHintsEnabled(val);
    await setHintsGloballyEnabled(val);
  };

  const handleResetHints = async () => {
    await resetHintsDismissal();
    await setHintsGloballyEnabled(true);
    setHintsEnabled(true);
    CustomAlert.alert("Hints Reset", "Product hints will now be displayed again at the top of the app.");
  };

  const uploadAndSetProfilePhoto = async (source: any) => {
    if (!services) {
      CustomAlert.alert("Error", "Firebase services are not available.");
      return;
    }

    try {
      setUploadingPhoto(true);

      const variants = await createProfileImageVariants(source);
      const res = await updateProfilePicture(services, variants);

      if (res && res.status) {
        if (res.photoUrl) {
          setPhotoUrl(res.photoUrl);
          if (services.auth.currentUser) {
            try {
              await updateProfile(services.auth.currentUser, { photoURL: res.photoUrl });
            } catch (clientAuthErr) {
              console.warn("Could not update client auth photoURL:", clientAuthErr);
            }
          }
        }
        await refreshUser();
      } else {
        CustomAlert.alert("Upload Blocked", res?.error || "This image violates Memories safety policies.");
      }
    } catch (err: any) {
      console.error("Error updating profile picture:", err);
      CustomAlert.alert("Upload Error", err.message || "Failed to upload profile picture.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleProfilePhotoSelected = async (assets: any[]) => {
    const asset = assets?.[0];
    if (!asset?.uri) return;
    await uploadAndSetProfilePhoto(asset.uri);
  };

  const handleProfilePhotoCaptured = async (file: File | any) => {
    if (!file) return;
    const source = file.uri ? file.uri : file;
    await uploadAndSetProfilePhoto(source);
  };

  const handleProfileMediaSelected = async (media: MediaModel) => {
    if (!services || !media.id) return;
    try {
      setUploadingPhoto(true);
      setShowMediaSelector(false);
      const res = await updateProfilePicture(services, { mediaId: media.id });
      if (res && res.status) {
        if (res.photoUrl) {
          setPhotoUrl(res.photoUrl);
          if (services.auth?.currentUser) {
            try {
              await updateProfile(services.auth.currentUser, { photoURL: res.photoUrl });
            } catch (clientAuthErr) {
              console.warn("Could not update client auth photoURL:", clientAuthErr);
            }
          }
        }
        await refreshUser();
      } else {
        CustomAlert.alert("Upload Blocked", res?.error || "Failed to set profile picture from media.");
      }
    } catch (err: any) {
      console.error("Error setting profile picture from media:", err);
      CustomAlert.alert("Upload Error", err.message || "Failed to set profile picture.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  /**
   * Opens the publicly accessible Privacy Policy web page.
   */
  const handleOpenPrivacyPolicy = async () => {
    try {
      await Linking.openURL(privacyPolicyUrl);
    } catch (err) {
      console.error("Failed to open Privacy Policy URL:", err);
      CustomAlert.alert("Privacy Policy", `Please visit: ${privacyPolicyUrl}`);
    }
  };

  /**
   * Opens the publicly accessible About Memories web page.
   */
  const handleOpenAbout = async () => {
    try {
      await Linking.openURL(aboutUrl);
    } catch (err) {
      console.error("Failed to open About URL:", err);
      CustomAlert.alert("About Memories", `Please visit: ${aboutUrl}`);
    }
  };

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const handleCancelEdit = () => {
    setIsEditing(false);
    setDisplayName(user?.displayName || "");
    setEmail(user?.email || "");
    setReauthPassword("");
    setNameError("");
    setEmailError("");
    setReauthPasswordError("");
  };

  useEffect(() => {
    if (resetPassword === "true" && services?.auth?.currentUser) {
      setShowPasswordModal(true);
      const checkClaims = async () => {
        try {
          const firebaseUser = services.auth.currentUser;
          if (firebaseUser) {
            // Force refresh ID token to get the latest custom claims containing passwordResetClaim
            const tokenResult = await firebaseUser.getIdTokenResult(true);
            if (tokenResult.claims.passwordResetClaim === true) {
              setIsResettingPassword(true);
            }
          }
        } catch (err) {
          console.error("Error verifying passwordResetClaim from ID token:", err);
        }
      };
      checkClaims();
    }
  }, [resetPassword, services]);
  const [savingPassword, setSavingPassword] = useState(false);

  const cardBgColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const modalOverlayColor = "rgba(0,0,0,0.5)";
  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "badgeBackground" as any) || "rgba(150, 150, 150, 0.1)";
  const textColor = useThemeColor({}, "text");

  // Google Drive Media Storage Provider states
  const [showDriveConnectModal, setShowDriveConnectModal] = useState(false);
  const [showDriveDisconnectModal, setShowDriveDisconnectModal] = useState(false);
  const [driveFolderId, setDriveFolderId] = useState("");
  const [driveFolderName, setDriveFolderName] = useState("");
  const [driveFolderIdError, setDriveFolderIdError] = useState("");
  const [driveFolderNameError, setDriveFolderNameError] = useState("");
  const [grantDriveReadAccess, setGrantDriveReadAccess] = useState(false);
  const [savingDriveConfig, setSavingDriveConfig] = useState(false);

  const isGoogleAuth = Boolean(
    user?.authProvider === "google" ||
    user?.providerIds?.includes("google.com") ||
    user?.providerIds?.includes("google") ||
    (typeof user?.email === "string" && user.email.toLowerCase().endsWith("@gmail.com"))
  );

  const isGoogleDriveConnected = Boolean(
    user?.storageProvider === "google_drive" &&
    user?.googleDriveConfig?.folderId
  );

  const handleOpenConnectDrive = () => {
    if (!isGoogleAuth) {
      CustomAlert.alert(
        "Google Authentication Required",
        "Connecting Google Drive storage requires signing in with Google/Gmail authentication. Please sign in with your Google account."
      );
      return;
    }
    setDriveFolderId(user?.googleDriveConfig?.folderId || "");
    setDriveFolderName(user?.googleDriveConfig?.folderName || "");
    setDriveFolderIdError("");
    setDriveFolderNameError("");
    setGrantDriveReadAccess(false);
    setShowDriveConnectModal(true);
  };

  const handleConnectDriveSubmit = async () => {
    setDriveFolderIdError("");
    setDriveFolderNameError("");

    let hasError = false;
    if (!driveFolderId || !driveFolderId.trim()) {
      setDriveFolderIdError("Please enter your Google Drive Folder ID.");
      hasError = true;
    }
    if (!driveFolderName || !driveFolderName.trim()) {
      setDriveFolderNameError("Please enter a name for this folder.");
      hasError = true;
    }
    if (!grantDriveReadAccess) {
      CustomAlert.alert(
        "Permission Required",
        "You must allow Memories read access to this folder to connect it."
      );
      hasError = true;
    }

    if (hasError) return;

    setSavingDriveConfig(true);
    try {
      const res = await connectGoogleDriveStorage(services, {
        folderId: driveFolderId.trim(),
        folderName: driveFolderName.trim(),
      });

      if (res?.status) {
        await refreshUser();
        setShowDriveConnectModal(false);
        CustomAlert.alert(
          "Google Drive Connected",
          `Successfully connected to "${driveFolderName.trim()}". All new media uploads will now be stored in this Google Drive folder.`
        );
      } else {
        throw new Error((res as any)?.error || "Failed to connect Google Drive.");
      }
    } catch (err: any) {
      console.error("[Profile] Failed to connect Google Drive storage:", err);
      CustomAlert.alert("Connection Failed", err.message || "Failed to connect to Google Drive.");
    } finally {
      setSavingDriveConfig(false);
    }
  };

  const handleConfirmDisconnectDrive = async () => {
    setSavingDriveConfig(true);
    try {
      const res = await disconnectGoogleDriveStorage(services);
      if (res?.status) {
        await refreshUser();
        setShowDriveDisconnectModal(false);
        CustomAlert.alert(
          "Google Drive Disconnected",
          "Media storage has been reverted to Memories Cloud Storage. Previously uploaded items in your Google Drive folder are now marked as unavailable until reconnected."
        );
      } else {
        throw new Error((res as any)?.error || "Failed to disconnect Google Drive.");
      }
    } catch (err: any) {
      console.error("[Profile] Failed to disconnect Google Drive storage:", err);
      CustomAlert.alert("Error", err.message || "Failed to disconnect Google Drive.");
    } finally {
      setSavingDriveConfig(false);
    }
  };

  if (!user) return null;

  // Detect if user has a password provider
  const hasPasswordProvider = (user as any)?.providerData?.some((p: any) => p.providerId === "password");

  /**
   * Validates and submits the profile changes (name and email).
   */
  const handleSaveProfile = async () => {
    if (!user) return;
    if (!services?.auth) {
      CustomAlert.alert("Error", "Firebase Auth is not initialized.");
      return;
    }

    setNameError("");
    setEmailError("");
    setReauthPasswordError("");

    const trimmedName = displayName.trim();
    const trimmedEmail = email.trim().toLowerCase();

    let hasError = false;
    if (!trimmedName) {
      setNameError("Name cannot be empty.");
      hasError = true;
    }

    if (!trimmedEmail) {
      setEmailError("Email cannot be empty.");
      hasError = true;
    }

    const nameChanged = trimmedName !== (user.displayName || "");
    const emailChanged = trimmedEmail !== (user.email || "");

    // If email changed, check if they are password-based and require reauthentication
    if (emailChanged && hasPasswordProvider && !reauthPassword) {
      setReauthPasswordError("Please enter your current password to change your email address.");
      hasError = true;
    }

    if (hasError) {
      return;
    }

    if (!nameChanged && !emailChanged) {
      setIsEditing(false);
      return;
    }

    setSavingProfile(true);
    const firebaseUser = services.auth.currentUser as User;

    try {
      // 1. If email changed, reauthenticate if they have a password, then update email
      if (emailChanged) {
        if (hasPasswordProvider) {
          const credential = EmailAuthProvider.credential(firebaseUser.email!, reauthPassword);
          await reauthenticateWithCredential(firebaseUser, credential);
        }
        await updateEmail(firebaseUser, trimmedEmail);
      }

      // 2. If name changed, update profile and sync database
      if (nameChanged) {
        await updateProfile(firebaseUser, { displayName: trimmedName });
        // Sync display name with backend Firestore database
        try {
          await updateUserFunc(services, { uid: firebaseUser.uid, displayName: trimmedName });
        } catch (dbErr) {
          console.error("Failed to sync updated name with Firestore:", dbErr);
        }
      }

      // 3. Refresh user state in context to re-render UI
      await refreshUser();

      setIsEditing(false);
      setReauthPassword("");
    } catch (err: any) {
      console.error("Error saving profile:", err);
      CustomAlert.alert("Error", err.message || "Failed to update profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  /**
   * Validates and submits the password change or linking action.
   */
  const handlePasswordSubmit = async () => {
    setOldPasswordError("");
    setNewPasswordError("");
    setConfirmPasswordError("");

    let hasError = false;
    const needsOldPassword = hasPasswordProvider && !isResettingPassword;
    if (needsOldPassword && !oldPassword) {
      setOldPasswordError("Please enter your current password.");
      hasError = true;
    }
    if (!newPassword) {
      setNewPasswordError("Please enter a new password.");
      hasError = true;
    }
    if (!confirmPassword) {
      setConfirmPasswordError("Please repeat your new password.");
      hasError = true;
    } else if (newPassword && newPassword !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match.");
      hasError = true;
    }

    // Validate strict password policy
    if (newPassword) {
      const policyResult = validatePasswordPolicy(newPassword);
      if (!policyResult.isValid) {
        setNewPasswordError(policyResult.errorMessage || "Password does not meet requirements.");
        hasError = true;
      }
    }

    if (hasError) {
      return;
    }

    if (!services?.auth) {
      CustomAlert.alert("Error", "Firebase Auth is not initialized.");
      return;
    }

    setSavingPassword(true);
    const firebaseUser = services.auth.currentUser as User;

    try {
      if (hasPasswordProvider) {
        // Reauthenticate and update password
        if (needsOldPassword) {
          const credential = EmailAuthProvider.credential(firebaseUser.email!, oldPassword);
          await reauthenticateWithCredential(firebaseUser, credential);
        }
        await updatePassword(firebaseUser, newPassword);
      } else {
        // Link the email/password provider to their magic link account
        const credential = EmailAuthProvider.credential(firebaseUser.email!, newPassword);
        await linkWithCredential(firebaseUser, credential);
      }

      // Clean states and close modal
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setIsResettingPassword(false);
      setShowPasswordModal(false);
    } catch (err: any) {
      console.error("Error changing/setting password:", err);
      CustomAlert.alert("Error", err.message || "Failed to update password.");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <ThemedScreen
      title="Profile"
      showNavigationTitle={true}
      leftIcon={{
        name: "arrow-back",
        onPress: () => (router.canGoBack() ? router.back() : router.navigate("/(tabs)" as any)),
      }}
      rightIcon={null}
    >
      <ThemedView style={[styles.card, { backgroundColor: cardBgColor, borderColor: borderColor }]}>
        <View style={styles.avatarWrapper}>
          <View style={styles.avatarContainer}>
            {photoUrl || user?.photoUrl || (user as any)?.photoURL ? (
              <Image
                source={{ uri: photoUrl || user?.photoUrl || (user as any)?.photoURL }}
                style={styles.avatarImage}
                resizeMode="cover"
              />
            ) : (
              <ThemedText style={styles.avatarText}>{(displayName || email || "?")[0].toUpperCase()}</ThemedText>
            )}
            <UploadMedia
              onMediaSelected={handleProfilePhotoSelected}
              allowsEditing
              aspect={[1, 1]}
              quality={0.9}
              disabled={uploadingPhoto}
              uploading={uploadingPhoto}
              renderTrigger={(onPress, disabled) => (
                <TouchableOpacity
                  style={styles.avatarBadge}
                  onPress={onPress}
                  disabled={disabled}
                  accessibilityRole="button"
                  accessibilityLabel="Upload profile picture"
                >
                  {uploadingPhoto ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Ionicons name="camera" size={20} color="#FFFFFF" />
                  )}
                </TouchableOpacity>
              )}
            />
          </View>

          <View style={styles.photoActionsRow}>
            <UploadMedia
              onMediaSelected={handleProfilePhotoSelected}
              allowsEditing
              aspect={[1, 1]}
              quality={0.9}
              disabled={uploadingPhoto}
              uploading={uploadingPhoto}
              renderTrigger={(onPress, disabled) => (
                <TouchableOpacity
                  style={[styles.photoActionButton, { borderColor: tintColor }]}
                  onPress={onPress}
                  disabled={disabled}
                  accessibilityRole="button"
                  accessibilityLabel="Choose profile photo from library"
                >
                  <Ionicons name="images-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
                  <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                    {uploadingPhoto
                      ? "Uploading..."
                      : photoUrl || user?.photoUrl || (user as any)?.photoURL
                      ? "Choose Photo"
                      : "Upload Photo"}
                  </ThemedText>
                </TouchableOpacity>
              )}
            />

            <CapturePhoto
              onCapture={handleProfilePhotoCaptured}
              disabled={uploadingPhoto}
              uploading={uploadingPhoto}
              initialCameraType="front"
              renderTrigger={(onPress, disabled) => (
                <TouchableOpacity
                  style={[styles.photoActionButton, { borderColor: tintColor }]}
                  onPress={onPress}
                  disabled={disabled}
                  accessibilityRole="button"
                  accessibilityLabel="Take profile photo with camera"
                >
                  <Ionicons name="camera-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
                  <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                    Take Photo
                  </ThemedText>
                </TouchableOpacity>
              )}
            />

            <TouchableOpacity
              style={[styles.photoActionButton, { borderColor: tintColor }]}
              onPress={() => setShowMediaSelector(true)}
              disabled={uploadingPhoto}
              accessibilityRole="button"
              accessibilityLabel="Select profile photo from media library"
              testID="profile-select-media-btn"
            >
              <Ionicons name="folder-open-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
              <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                Select Media
              </ThemedText>
            </TouchableOpacity>
          </View>
        </View>

        {isEditing ? (
          <View
            style={styles.editForm}
            role="form"
            accessibilityRole="form"
            {...(Platform.OS === "web" ? { onSubmit: (e: any) => e.preventDefault() } : {})}
          >
            <View style={styles.inputWrapper}>
              <ThemedText type="fieldLabel">Name</ThemedText>
              <ThemedInput
                value={displayName}
                onChangeText={(val) => {
                  setDisplayName(val);
                  if (nameError) setNameError("");
                }}
                placeholder="Enter your name"
                autoCapitalize="words"
                error={nameError}
              />
            </View>

            <View style={styles.inputWrapper}>
              <ThemedText type="fieldLabel">Email Address</ThemedText>
              <ThemedInput
                value={email}
                onChangeText={(val) => {
                  setEmail(val);
                  if (emailError) setEmailError("");
                }}
                placeholder="Enter your email"
                autoCapitalize="none"
                keyboardType="email-address"
                error={emailError}
              />
            </View>

            {email.trim().toLowerCase() !== (user.email || "").trim().toLowerCase() && hasPasswordProvider && (
              <View style={styles.inputWrapper}>
                <ThemedText type="fieldLabel">Current Password (Required to change email)</ThemedText>
                <ThemedInput
                  value={reauthPassword}
                  onChangeText={(val) => {
                    setReauthPassword(val);
                    if (reauthPasswordError) setReauthPasswordError("");
                  }}
                  secureTextEntry
                  placeholder="Enter current password"
                  error={reauthPasswordError}
                  autoComplete="current-password"
                  textContentType="password"
                />
              </View>
            )}
          </View>
        ) : (
          <>
            <ThemedText type="subtitle" style={styles.nameText}>
              {user.displayName || "User Profile"}
            </ThemedText>
            <ThemedText style={styles.emailText}>{user.email}</ThemedText>

            <View style={styles.statusRow}>
              <View style={[styles.statusBadge, user.emailVerified ? styles.statusVerified : styles.statusUnverified]}>
                <ThemedText style={[styles.statusBadgeText, user.emailVerified ? { color: "#22C55E" } : { color: "#EF4444" }]}>{user.emailVerified ? "Verified Email" : "Unverified"}</ThemedText>
              </View>
              {user.betaGroup ? (
                <View
                  style={[
                    styles.statusBadge,
                    {
                      backgroundColor: "rgba(5, 150, 105, 0.1)",
                      borderColor: "#059669",
                      borderWidth: 1,
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 4,
                    },
                  ]}
                  testID="profile-beta-group-badge"
                  accessibilityLabel={`Beta Group: ${user.betaGroup}`}
                >
                  <Ionicons name="flask-outline" size={13} color="#059669" />
                  <ThemedText style={[styles.statusBadgeText, { color: "#059669", fontWeight: "700" }]}>
                    {`Beta Group: ${user.betaGroup}`}
                  </ThemedText>
                </View>
              ) : null}
            </View>
          </>
        )}
      </ThemedView>

      <ThemedView style={styles.actionsContainer}>
        {isEditing ? (
          savingProfile ? (
            <ActivityIndicator size="small" color={tintColor} style={styles.loader} />
          ) : (
            <>
              <ThemedButton onPress={handleSaveProfile} title="Save Changes" variant="primary" />
              <ThemedButton onPress={handleCancelEdit} title="Cancel" variant="minimal" />
            </>
          )
        ) : (
          <>
            <ThemedButton onPress={() => setIsEditing(true)} title="Edit Profile" variant="primary" />

            {!user.emailVerified ? (
              <ThemedButton
                onPress={async () => {
                  await emailVerification();
                  setVerified(true);
                }}
                title={verified ? "Check Emulator output to click link!" : "Verify Email"}
                variant="secondary"
              />
            ) : null}

            <ThemedButton onPress={() => setShowPasswordModal(true)} title={hasPasswordProvider ? "Change Password" : "Set Password"} variant="secondary" />

            <ThemedButton
              onPress={() => router.push("/quota")}
              title="Storage & Quota"
              variant="secondary"
            />

            <ThemedButton
              onPress={() => setShowReportModal(true)}
              title="Report inappropriate content"
              variant="secondary"
            />

            <ThemedButton
              onPress={async () => {
                await signOut();
                // Navigate after signing in.
                // TODO: You may want to tweak this to ensure sign-in is successful before navigating.
                router.replace("/");
              }}
              title="Sign Out"
              variant="danger"
            />

            <ThemedButton
              onPress={() => router.push("/terminate-account")}
              title="Terminate Account"
              variant="danger"
            />

            <ThemedButton title="Go Back" onPress={() => router.canGoBack() && router.back()} variant="minimal" style={styles.backBtn} />
          </>
        )}
      </ThemedView>

      {/* Product Hints & Tips Preferences Section */}
      <ThemedView style={[styles.platformCard, { backgroundColor: cardBgColor, borderColor: borderColor, marginBottom: 16 }]}>
        <View style={styles.sectionHeaderRow}>
          <Ionicons name="bulb-outline" size={20} color={tintColor} />
          <ThemedText type="subtitle" style={styles.platformSectionTitle}>Product Hints & Tips</ThemedText>
        </View>

        <ThemedText style={styles.privacySubtext}>
          Display helpful product tips, archival shortcuts, and feature guides hovering at the top of the app.
        </ThemedText>

        <View style={{ marginTop: 12 }}>
          <ThemedToggle
            value={hintsEnabled}
            onValueChange={handleToggleHints}
            label="Show Product Hints"
            sublabel={hintsEnabled ? "Hints are active and hover at the top of screens" : "Hints are turned off"}
          />
        </View>

        <View style={{ marginTop: 12 }}>
          <ThemedButton
            title="Reset Dismissed Hints"
            variant="secondary"
            onPress={handleResetHints}
          />
        </View>
      </ThemedView>

      {/* GPS & Location Permissions Section */}
      <ThemedView style={[styles.platformCard, { backgroundColor: cardBgColor, borderColor: borderColor, marginBottom: 16 }]}>
        <View style={styles.sectionHeaderRow}>
          <Ionicons name="navigate-outline" size={20} color={tintColor} />
          <ThemedText type="subtitle" style={styles.platformSectionTitle}>GPS & Location Permissions</ThemedText>
        </View>

        <ThemedText style={styles.privacySubtext}>
          Memories uses high-accuracy GPS coordinates to automatically tag memories, media, and location pins with precise geographic coordinates.
        </ThemedText>

        <View style={styles.platformInfoGrid}>
          <View style={styles.platformRow}>
            <ThemedText style={styles.platformLabel}>Permission Status</ThemedText>
            <ThemedText
              style={[
                styles.platformValue,
                {
                  color: gpsStatus?.granted ? "#10B981" : (gpsStatus?.status === "denied" ? "#EF4444" : "#F59E0B"),
                  fontWeight: "700",
                },
              ]}
            >
              {gpsStatus?.granted
                ? "Granted"
                : gpsStatus?.status === "denied"
                ? "Denied"
                : "Not Granted"}
            </ThemedText>
          </View>
          <View style={styles.platformRow}>
            <ThemedText style={styles.platformLabel}>Device Location Service</ThemedText>
            <ThemedText
              style={[
                styles.platformValue,
                {
                  color: gpsStatus?.gpsEnabled ? "#10B981" : "#EF4444",
                  fontWeight: "600",
                },
              ]}
            >
              {gpsStatus?.gpsEnabled ? "Enabled" : "Disabled (Turn on GPS)"}
            </ThemedText>
          </View>
        </View>

        <View style={{ marginTop: 14 }}>
          <ThemedButton
            title={requestingGps ? "Requesting Permissions..." : "Request GPS Permissions"}
            variant="primary"
            onPress={handleForceRequestGps}
            disabled={requestingGps}
            accessibilityLabel="Force prompt for GPS permissions"
          />
        </View>

        {Platform.OS !== "web" && (
          <View style={{ marginTop: 8 }}>
            <ThemedButton
              title="Open Device Location Settings"
              variant="secondary"
              onPress={() => Linking.openSettings()}
              accessibilityLabel="Open device settings"
            />
          </View>
        )}
      </ThemedView>

      {/* Analytics & Telemetry Privacy Section */}
      <ThemedView style={[styles.platformCard, { backgroundColor: cardBgColor, borderColor: borderColor, marginBottom: 16 }]}>
        <View style={styles.sectionHeaderRow}>
          <Ionicons name="shield-checkmark-outline" size={20} color={tintColor} />
          <ThemedText type="subtitle" style={styles.platformSectionTitle}>Analytics & Telemetry Privacy</ThemedText>
        </View>

        <ThemedText style={styles.privacySubtext}>
          We respect your privacy under the ePrivacy Directive and GDPR. Analytical telemetry helps us diagnose performance bottlenecks and improve features. No data is collected without your explicit consent.
        </ThemedText>

        <View style={styles.platformInfoGrid}>
          <View style={styles.platformRow}>
            <ThemedText style={styles.platformLabel}>Consent Status</ThemedText>
            <ThemedText
              style={[
                styles.platformValue,
                {
                  color: analyticsConsent ? "#10B981" : "#F59E0B",
                  fontWeight: "700",
                },
              ]}
              testID="analytics-consent-status"
            >
              {analyticsConsent ? "Granted" : "Revoked / Not Granted"}
            </ThemedText>
          </View>
        </View>

        <View style={{ marginTop: 12 }}>
          <ThemedToggle
            value={analyticsConsent}
            onValueChange={handleToggleAnalyticsConsent}
            disabled={savingConsent}
            label="Allow Analytical Telemetry"
            sublabel={
              analyticsConsent
                ? "Telemetry is enabled. You can revoke consent at any time."
                : "Telemetry is deferred and disabled until granted."
            }
          />
        </View>

        {analyticsConsent ? (
          <View style={{ marginTop: 12 }}>
            <ThemedButton
              title="Revoke Analytics Consent"
              variant="secondary"
              onPress={() => handleToggleAnalyticsConsent(false)}
              disabled={savingConsent}
              accessibilityLabel="Revoke analytics consent"
            />
          </View>
        ) : null}
      </ThemedView>

      {/* External Media Storage Provider (Google Drive) */}
      <ThemedView style={[styles.platformCard, { backgroundColor: cardBgColor, borderColor: borderColor, marginBottom: 16 }]} testID="google-drive-storage-section">
        <View style={styles.sectionHeaderRow}>
          <Ionicons name="logo-google" size={20} color={tintColor} />
          <ThemedText type="subtitle" style={styles.platformSectionTitle}>External Media Storage</ThemedText>
        </View>

        <ThemedText style={styles.privacySubtext}>
          Connect a personal Google Drive folder to store your media uploads directly in your Google Drive instead of Memories Cloud Storage.
        </ThemedText>

        <View style={styles.platformInfoGrid}>
          <View style={styles.platformRow}>
            <ThemedText style={styles.platformLabel}>Active Storage Provider</ThemedText>
            <ThemedText
              style={[
                styles.platformValue,
                {
                  color: isGoogleDriveConnected ? "#10B981" : tintColor,
                  fontWeight: "700",
                },
              ]}
              testID="active-storage-provider-status"
            >
              {isGoogleDriveConnected ? "Google Drive" : "Memories Cloud (Default)"}
            </ThemedText>
          </View>

          {isGoogleDriveConnected && user?.googleDriveConfig ? (
            <>
              <View style={styles.platformRow}>
                <ThemedText style={styles.platformLabel}>Connected Folder</ThemedText>
                <ThemedText style={[styles.platformValue, { fontWeight: "600" }]} testID="google-drive-folder-name">
                  {user.googleDriveConfig.folderName}
                </ThemedText>
              </View>
              <View style={styles.platformRow}>
                <ThemedText style={styles.platformLabel}>Folder ID</ThemedText>
                <ThemedText style={[styles.platformValue, styles.monoText]} numberOfLines={1} ellipsizeMode="middle" testID="google-drive-folder-id">
                  {user.googleDriveConfig.folderId}
                </ThemedText>
              </View>
            </>
          ) : null}
        </View>

        {isGoogleDriveConnected ? (
          <View style={{ marginTop: 14, gap: 8 }}>
            <View style={[styles.driveWarningCallout, { borderColor: "#DC3545", backgroundColor: "rgba(220, 53, 69, 0.08)" }]}>
              <Ionicons name="warning-outline" size={16} color="#DC3545" style={{ marginTop: 2 }} />
              <ThemedText style={{ fontSize: 11.5, color: "#DC3545", flex: 1, lineHeight: 16 }}>
                All new media uploads are stored in this Google Drive folder. If you disconnect, switch folders, or delete the folder, existing media will become unavailable.
              </ThemedText>
            </View>
            <View style={{ flexDirection: "row", gap: 8, marginTop: 4 }}>
              <ThemedButton
                title="Change Folder"
                variant="secondary"
                onPress={handleOpenConnectDrive}
                style={{ flex: 1 }}
                accessibilityLabel="Connect to a different Google Drive folder"
                testID="change-google-drive-btn"
              />
              <ThemedButton
                title="Disconnect"
                variant="secondary"
                onPress={() => setShowDriveDisconnectModal(true)}
                style={{ flex: 1 }}
                accessibilityLabel="Disconnect Google Drive media storage"
                testID="disconnect-google-drive-btn"
              />
            </View>
          </View>
        ) : (
          <View style={{ marginTop: 14 }}>
            {!isGoogleAuth ? (
              <View style={[styles.driveWarningCallout, { borderColor: "#F59E0B", backgroundColor: "rgba(245, 158, 11, 0.08)" }]}>
                <Ionicons name="information-circle-outline" size={16} color="#F59E0B" style={{ marginTop: 2 }} />
                <ThemedText style={{ fontSize: 11.5, color: "#F59E0B", flex: 1, lineHeight: 16 }}>
                  Google Drive storage requires logging in with Google/Gmail authentication.
                </ThemedText>
              </View>
            ) : null}
            <View style={{ marginTop: isGoogleAuth ? 0 : 8 }}>
              <ThemedButton
                title="Connect to Google Drive"
                variant="primary"
                onPress={handleOpenConnectDrive}
                disabled={!isGoogleAuth}
                accessibilityLabel="Connect to Google Drive storage"
                testID="connect-google-drive-btn"
              />
            </View>
          </View>
        )}
      </ThemedView>

      {/* Platform Section */}
      <PlatformDetails
        user={user}
        clientEnv={clientEnv}
        serverEnv={serverEnv}
      />

      {/* Password Change / Link Dialog Modal */}
      <Modal visible={showPasswordModal} animationType="fade" transparent={true} onRequestClose={() => setShowPasswordModal(false)}>
        <View style={[styles.modalOverlay, { backgroundColor: modalOverlayColor }]}>
          <ThemedView
            style={[styles.modalContent, { backgroundColor: cardBgColor, borderColor: borderColor, borderWidth: 1 }]}
            role="form"
            accessibilityRole="form"
            {...(Platform.OS === "web" ? { onSubmit: (e: any) => e.preventDefault() } : {})}
          >
            <ThemedText type="subtitle" style={styles.modalTitle}>
              {hasPasswordProvider ? "Change Account Password" : "Create Account Password"}
            </ThemedText>

            {hasPasswordProvider && !isResettingPassword && (
              <View style={styles.inputWrapper}>
                <ThemedText type="fieldLabel">Current Password</ThemedText>
                <ThemedInput
                  value={oldPassword}
                  onChangeText={(val) => {
                    setOldPassword(val);
                    if (oldPasswordError) setOldPasswordError("");
                  }}
                  secureTextEntry
                  placeholder="Enter current password"
                  error={oldPasswordError}
                  autoComplete="current-password"
                  textContentType="password"
                />
              </View>
            )}

            <View style={styles.inputWrapper}>
              <ThemedText type="fieldLabel">New Password</ThemedText>
              <ThemedInput
                value={newPassword}
                onChangeText={(val) => {
                  setNewPassword(val);
                  if (newPasswordError) setNewPasswordError("");
                }}
                secureTextEntry
                placeholder="Min 6 chars (A-Z, a-z, 0-9, special)"
                error={newPasswordError}
                autoComplete="new-password"
                textContentType="newPassword"
              />
            </View>

            <View style={styles.inputWrapper}>
              <ThemedText type="fieldLabel">Repeat New Password</ThemedText>
              <ThemedInput
                value={confirmPassword}
                onChangeText={(val) => {
                  setConfirmPassword(val);
                  if (confirmPasswordError) setConfirmPasswordError("");
                }}
                secureTextEntry
                placeholder="Repeat new password"
                error={confirmPasswordError}
                autoComplete="new-password"
                textContentType="newPassword"
              />
            </View>

            {savingPassword ? (
              <ActivityIndicator size="small" color={tintColor} style={styles.modalLoader} />
            ) : (
              <View style={styles.modalBtnRow}>
                <ThemedButton
                  title="Cancel"
                  variant="minimal"
                  onPress={() => {
                    setOldPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setOldPasswordError("");
                    setNewPasswordError("");
                    setConfirmPasswordError("");
                    setIsResettingPassword(false);
                    setShowPasswordModal(false);
                  }}
                />

                <TouchableOpacity style={[styles.modalBtn, styles.submitBtn, { backgroundColor: tintColor }]} onPress={handlePasswordSubmit}>
                  <ThemedText style={styles.submitBtnText}>{hasPasswordProvider ? "Update" : "Set Password"}</ThemedText>
                </TouchableOpacity>
              </View>
            )}
          </ThemedView>
        </View>
      </Modal>

      {/* Connect to Google Drive Modal */}
      <Modal visible={showDriveConnectModal} animationType="fade" transparent={true} onRequestClose={() => setShowDriveConnectModal(false)}>
        <View style={[styles.modalOverlay, { backgroundColor: modalOverlayColor }]}>
          <ThemedView style={[styles.modalContent, { backgroundColor: cardBgColor, borderColor: borderColor, borderWidth: 1 }]} testID="connect-drive-modal">
            <ThemedText type="subtitle" style={styles.modalTitle}>
              Connect to Google Drive
            </ThemedText>
            <ThemedText style={[styles.privacySubtext, { marginBottom: 14 }]}>
              Enter your Google Drive folder details below. New media uploads will be stored directly in this folder.
            </ThemedText>

            <View style={styles.inputWrapper}>
              <ThemedText type="fieldLabel">Folder Name</ThemedText>
              <ThemedInput
                value={driveFolderName}
                onChangeText={(val) => {
                  setDriveFolderName(val);
                  if (driveFolderNameError) setDriveFolderNameError("");
                }}
                placeholder="e.g. Memories Photos"
                error={driveFolderNameError}
                testID="input-drive-folder-name"
              />
            </View>

            <View style={styles.inputWrapper}>
              <ThemedText type="fieldLabel">Folder ID</ThemedText>
              <ThemedInput
                value={driveFolderId}
                onChangeText={(val) => {
                  setDriveFolderId(val);
                  if (driveFolderIdError) setDriveFolderIdError("");
                }}
                placeholder="Google Drive alphanumeric folder ID"
                error={driveFolderIdError}
                testID="input-drive-folder-id"
              />
            </View>

            <View style={{ marginVertical: 8 }}>
              <ThemedToggle
                value={grantDriveReadAccess}
                onValueChange={setGrantDriveReadAccess}
                label="Allow Memories read access"
                sublabel="Allows Memories to read and display photos stored in this Google Drive folder."
                testID="toggle-drive-permission"
              />
            </View>

            {savingDriveConfig ? (
              <ActivityIndicator size="small" color={tintColor} style={styles.modalLoader} />
            ) : (
              <View style={styles.modalBtnRow}>
                <TouchableOpacity
                  style={[styles.modalBtn, { backgroundColor: badgeBg }]}
                  onPress={() => setShowDriveConnectModal(false)}
                >
                  <ThemedText style={[styles.cancelBtnText, { color: textColor }]}>Cancel</ThemedText>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.modalBtn, styles.submitBtn, { backgroundColor: tintColor }]}
                  onPress={handleConnectDriveSubmit}
                  testID="submit-connect-drive-btn"
                >
                  <ThemedText style={styles.submitBtnText}>Connect Folder</ThemedText>
                </TouchableOpacity>
              </View>
            )}
          </ThemedView>
        </View>
      </Modal>

      {/* Disconnect Google Drive Warning Modal */}
      <Modal visible={showDriveDisconnectModal} animationType="fade" transparent={true} onRequestClose={() => setShowDriveDisconnectModal(false)}>
        <View style={[styles.modalOverlay, { backgroundColor: modalOverlayColor }]}>
          <ThemedView style={[styles.modalContent, { backgroundColor: cardBgColor, borderColor: "#DC3545", borderWidth: 1 }]} testID="disconnect-drive-modal">
            <ThemedText type="subtitle" style={[styles.modalTitle, { color: "#DC3545" }]}>
              ⚠️ Disconnect Google Drive?
            </ThemedText>
            <ThemedText style={[styles.privacySubtext, { marginVertical: 12, lineHeight: 18 }]}>
              Disconnecting or switching your Google Drive storage folder will cause previously uploaded media in that folder to become unavailable.
              {"\n\n"}
              The files remain safe in your Google Drive, but cannot be viewed in Memories until the original folder is reconnected.
            </ThemedText>

            {savingDriveConfig ? (
              <ActivityIndicator size="small" color="#DC3545" style={styles.modalLoader} />
            ) : (
              <View style={styles.modalBtnRow}>
                <TouchableOpacity
                  style={[styles.modalBtn, { backgroundColor: badgeBg }]}
                  onPress={() => setShowDriveDisconnectModal(false)}
                >
                  <ThemedText style={[styles.cancelBtnText, { color: textColor }]}>Keep Connected</ThemedText>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.modalBtn, styles.submitBtn, { backgroundColor: "#DC3545" }]}
                  onPress={handleConfirmDisconnectDrive}
                  testID="confirm-disconnect-drive-btn"
                >
                  <ThemedText style={styles.submitBtnText}>Disconnect</ThemedText>
                </TouchableOpacity>
              </View>
            )}
          </ThemedView>
        </View>
      </Modal>

      {/* Report Inappropriate Content Dialog Modal */}
      <InappropriateContentModal
        visible={showReportModal}
        onClose={() => setShowReportModal(false)}
      />

      <MediaSelector
        visible={showMediaSelector}
        onClose={() => setShowMediaSelector(false)}
        onSelectSingleMedia={handleProfileMediaSelected}
        onSelectMedia={(items) => {
          if (items.length > 0) {
            handleProfileMediaSelected(items[items.length - 1]);
          }
        }}
        multiSelect={false}
        imagesOnly={true}
        allowedMediaTypes={["image"]}
        title="Select Profile Picture"
        testID="profile-media-selector"
      />
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: 16,
    borderRadius: 0,
    borderWidth: 1,
    alignItems: "center",
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 4,
    elevation: 1,
  },
  avatarWrapper: {
    alignItems: "center",
    marginBottom: 12,
  },
  avatarContainer: {
    width: 140,
    height: 140,
    borderRadius: 70,
    backgroundColor: "#6366F1",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    borderWidth: 2,
    borderColor: "rgba(99, 102, 241, 0.2)",
  },
  avatarImage: {
    width: 140,
    height: 140,
    borderRadius: 70,
    overflow: "hidden",
  },
  avatarBadge: {
    position: "absolute",
    bottom: 4,
    right: 4,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#2563EB",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: "#FFFFFF",
    elevation: 3,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
  },
  photoActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  photoActionButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderWidth: 1,
    borderRadius: 0,
  },
  photoActionIcon: {
    marginRight: 4,
  },
  photoActionText: {
    fontSize: 12,
    fontWeight: "600",
  },
  avatarText: {
    color: "#FFFFFF",
    fontSize: 48,
    fontWeight: "bold",
  },
  nameText: {
    marginBottom: 2,
    fontWeight: "700",
  },
  emailText: {
    opacity: 0.6,
    fontSize: 13,
    marginBottom: 12,
  },
  statusRow: {
    flexDirection: "row",
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 0,
  },
  statusVerified: {
    backgroundColor: "rgba(34, 197, 94, 0.1)",
  },
  statusUnverified: {
    backgroundColor: "rgba(239, 68, 68, 0.1)",
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: "600",
  },
  actionsContainer: {
    gap: 8,
    backgroundColor: "transparent",
  },
  backBtn: {
    marginTop: 12,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    width: "90%",
    maxWidth: 400,
    borderRadius: 0,
    borderWidth: 1,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  modalTitle: {
    marginBottom: 14,
    textAlign: "center",
    fontWeight: "bold",
  },
  inputWrapper: {
    marginBottom: 10,
  },
  modalLoader: {
    marginVertical: 16,
    alignSelf: "center",
  },
  modalBtnRow: {
    flexDirection: "row",
    gap: 8,
    marginTop: 10,
  },
  modalBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  submitBtn: {
    borderWidth: 0,
  },
  cancelBtnText: {
    fontSize: 13,
    fontWeight: "600",
  },
  submitBtnText: {
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: "bold",
  },
  editForm: {
    alignSelf: "stretch",
    marginTop: 6,
  },
  loader: {
    marginVertical: 12,
    alignSelf: "center",
  },
  platformCard: {
    borderRadius: 0,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 4,
    elevation: 1,
  },
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 12,
  },
  platformSectionTitle: {
    fontSize: 16,
    fontWeight: "700",
  },
  platformInfoGrid: {
    gap: 8,
  },
  platformRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 3,
  },
  platformLabel: {
    fontSize: 12,
    opacity: 0.75,
  },
  platformValue: {
    fontSize: 12,
    fontWeight: "600",
  },
  monoText: {
    fontFamily: "monospace",
    maxWidth: "60%",
  },
  platformDivider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: 12,
  },
  privacySection: {
    gap: 8,
  },
  privacyHeaderInline: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  privacyHeading: {
    fontSize: 14,
    fontWeight: "600",
  },
  privacySubtext: {
    fontSize: 12,
    opacity: 0.75,
    lineHeight: 16,
  },
  privacyActionRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 2,
  },
  privacyUrlLink: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexShrink: 1,
  },
  privacyUrlText: {
    fontSize: 12,
    fontWeight: "500",
    textDecorationLine: "underline",
  },
  viewPolicyBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 0,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  viewPolicyBtnText: {
    fontSize: 12,
    fontWeight: "600",
  },
  driveWarningCallout: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    padding: 10,
    borderRadius: 2,
    borderWidth: 1,
  },
});
